import { useViewApproveRouteDrawer } from '@/components/InvoiceReportDetailDrawer/ApprovalRouteDrawer';
import { BusinessDocumentsProvider } from '@/components/InvoiceReportDetailDrawer/ApplicationContent/context/BusinessDocumentsContext';
import { DEFAULT_SECTION_STATES } from '@/components/InvoiceReportDetailDrawer/constants';
import { PaymentDetailProvider } from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import { FileInfoDrawerProvider } from '@/components/InvoiceReportDetailDrawer/FileInfoDrawer/context/FileInfoDrawerContext';
import { useDrawerWidth } from '@/components/InvoiceReportDetailDrawer/hooks/useDrawerWidth';
import { useSectionCollapseStates } from '@/components/InvoiceReportDetailDrawer/hooks/useSectionCollapseStates';
import { ErrorBoundary } from '@/components/Rollbar';
import { ApprovalRouteDrawer } from '@/features/invoice_reports/components/ApprovalRouteDrawer';
import {
  ShareDrawer,
  useShareDrawer,
} from '@/features/invoice_reports/components/ShareDrawer';
import { BusinessDocumentDrawerContainer } from '@/features/invoice_reports/components/InvoiceReportDetailDrawer/BusinessDocumentDrawerContainer';
import { DrawerTitle } from '@/features/invoice_reports/components/InvoiceReportDetailDrawer/components/DrawerTitle';
import {
  useInvoiceReportSummaryAccessErrorEffect,
  useInvoiceReportSummaryData,
} from '@/features/invoice_reports/components/InvoiceReportDetailDrawer/hooks/useInvoiceReportSummaryData';
import { DEFAULT_DRAWER_DETAIL_WIDTH } from '@/features/invoice_reports/constants';
import { useAxisAuth2ndApiFeatureFlag } from '@/hooks/feature_flag/useAxisAuth2ndApiFeatureFlag';
import { useTranslation } from '@/i18n';
import {
  DraggableDrawer,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { InvoiceReportDetail } from './Detail';
import styles from './Drawer.module.scss';

const cx = classnames.bind(styles);

type ErrorFallbackProps = {
  error: Error | null;
  onClose?: VoidFunction;
};

const ErrorFallback: FC<ErrorFallbackProps> = ({ error, onClose }) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  useEffect(() => {
    onClose?.();
    notify.error({
      title: error?.message ?? t('system_error'),
      duration: 3,
    });
  }, [error, notify, onClose, t]);
  return <></>;
};
ErrorFallback.displayName = 'ErrorFallback';

export type InvoiceReportDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  selectedId: string;
  apPayeeName?: string | null;
};

export const InvoiceReportDetailDrawer: FC<InvoiceReportDetailDrawerProps> =
  memo(({ open, onClose, selectedId, apPayeeName }) => {
    const { width, setWidth } = useDrawerWidth(DEFAULT_DRAWER_DETAIL_WIDTH);
    const { data, isLoading, error } = useInvoiceReportSummaryData(selectedId);
    const {
      isOpen: approvalRouteOpen,
      onDrawerOpen: onOpenApprovalRoute,
      onDrawerClose: onCloseApprovalRoute,
    } = useViewApproveRouteDrawer();
    const {
      isOpen: shareOpen,
      onDrawerOpen: onOpenShareViewers,
      onDrawerClose: onCloseShare,
    } = useShareDrawer();
    const isAxisAuth2ndApiFeatureEnabled = useAxisAuth2ndApiFeatureFlag();

    const { states: sectionStates, onSectionToggle } = useSectionCollapseStates(
      DEFAULT_SECTION_STATES
    );

    /**
     * Capture whether a flash alert (Rails redirect error) was present in session
     * storage at the time this drawer first mounts. When the server redirects back
     * to the list page with ?id in the referer URL after a permission failure, both
     * a flash toast and a 403 from reportSummary would fire. We use this flag to
     * suppress the redundant 403 toast so only the flash toast is shown.
     */
    const [suppressForbiddenToast] = useState(
      () => !!sessionStorage.getItem('toast-alert-message')
    );

    useInvoiceReportSummaryAccessErrorEffect({
      error,
      onClose,
      suppressForbiddenToast,
    });

    const titleInfo = useMemo(
      () => ({ title: data?.title ?? '', number: data?.number ?? '' }),
      [data?.title, data?.number]
    );

    return (
      <PaymentDetailProvider>
        <FileInfoDrawerProvider>
          <BusinessDocumentsProvider>
            <ErrorBoundary
              fallback={({ error }) => (
                <ErrorFallback onClose={onClose} error={error} />
              )}
            >
              <DraggableDrawer
                width={width}
                setWidth={setWidth}
                visible={open}
                onClose={onClose}
                destroyOnClose
                title={
                  <DrawerTitle
                    titleInfo={titleInfo}
                    reportId={selectedId}
                    detailPath={data?.detail_path}
                    editPath={data?.edit_path}
                    editAction={data?.edit_action}
                    deleteAction={data?.delete_action}
                    cancelAction={data?.cancel_action}
                    shareAction={data?.share_action}
                    viewersCount={data?.viewers_count}
                    isCopyable={data?.is_copyable}
                    reportTotalAmount={data?.total_value}
                    apPayeeName={apPayeeName}
                    hasApprovalRoute={data?.can_display_approval_route ?? false}
                    onOpenApprovalRoute={onOpenApprovalRoute}
                    onOpenShareViewers={onOpenShareViewers}
                  />
                }
                contentContainerClassName={cx(styles['contentContainer'])}
                className={cx(styles['invoiceReportDetailDrawer'])}
                minWidth={DEFAULT_DRAWER_DETAIL_WIDTH}
              >
                <InvoiceReportDetail
                  summaryData={data}
                  isLoading={isLoading}
                  reportId={selectedId}
                  sectionStates={sectionStates}
                  onSectionToggle={onSectionToggle}
                />
              </DraggableDrawer>
              {approvalRouteOpen && (
                <ApprovalRouteDrawer
                  open={approvalRouteOpen}
                  reportId={selectedId}
                  onCloseDetailDrawer={onClose}
                  onClose={onCloseApprovalRoute}
                />
              )}
              {isAxisAuth2ndApiFeatureEnabled && (
                <ShareDrawer
                  open={shareOpen}
                  reportId={selectedId}
                  sharePath={data?.share_path}
                  onCloseDetailDrawer={onClose}
                  onClose={onCloseShare}
                />
              )}
              <BusinessDocumentDrawerContainer onCloseDetailDrawer={onClose} />
            </ErrorBoundary>
          </BusinessDocumentsProvider>
        </FileInfoDrawerProvider>
      </PaymentDetailProvider>
    );
  });

InvoiceReportDetailDrawer.displayName = 'InvoiceReportDetailDrawer';
